import React, { useState, useEffect } from 'react';
import {
  Save,
  RotateCcw,
  FileText,
  DollarSign,
  Users,
  Calendar,
  Code,
  MapPin,
  Briefcase,
  Settings,
  Cpu,
  Clock,
  UserPlus,
  Plus,
  X,
  Target,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  HelpCircle
} from 'lucide-react';
import { Breadcrumb } from '../../components/Common';
import { formatINR, formatRupeeAmount } from '../../utils/currencyFormat';
import HelpModal from '../../components/HelpModal/HelpModal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import autoIncrementService from '../../services/autoIncrementService';
import commercialProposalService from '../../services/commercialProposalService';
import { leadGenerationService } from '../../services/leadGenerationService';
import prospectMasterService from '../../services/prospectMasterService';
import '../../styles/components/commercial-proposal.css';

const CommercialProposalForm = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('proposalInfo');
  const [showHelp, setShowHelp] = useState(false);
  const [isLoadingProposalCode, setIsLoadingProposalCode] = useState(false);

  const [formData, setFormData] = useState({
    proposalCode: '',
    proposalDate: new Date().toISOString().split('T')[0],
    proposalTitle: '',
    proposalDescription: '',
    proposalType: 'Quote',
    proposalStatus: 'Draft',
    priority: 'Medium',
    lead: '',
    prospect: '',
    proposalScope: '',
    proposalObjectives: '',
    proposalDeliverables: [],
    proposalTimeline: '',
    proposalTerms: '',
    proposalConditions: '',
    totalValue: 0,
    currency: 'INR',
    taxRate: 18,
    discountPercentage: 0,
    paymentTerms: 'Advance',
    paymentSchedule: [],
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: '',
    services: [],
    products: [],
    proposalOwner: '',
    proposalTeam: [],
    proposalManager: '',
    clientContact: {
      name: '',
      email: '',
      phone: '',
      designation: ''
    },
    clientDecisionMaker: {
      name: '',
      email: '',
      phone: '',
      designation: ''
    },
    clientRequirements: '',
    clientBudget: 0,
    clientTimeline: '',
    competitors: [],
    competitiveAdvantage: '',
    uniqueSellingPoints: [],
    followUpRequired: false,
    followUpDate: '',
    followUpNotes: '',
    nextSteps: '',
    expectedDecisionDate: '',
    probability: 0,
    attachments: [],
    proposalTemplate: '',
    approvalRequired: false
  });

  const [leads, setLeads] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadProposalCode();
    loadMasterData();
  }, []);

  const loadProposalCode = async () => {
    try {
      setIsLoadingProposalCode(true);
      const response = await autoIncrementService.getCurrentValue('COMM-PROP');
      if (response.success) {
        setFormData((prev) => ({
          ...prev,
          proposalCode: response.data.formattedValue,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          proposalCode: 'CP-0001', // Default placeholder
        }));
        console.warn('Auto-increment sequence COMM-PROP not found, using placeholder');
      }
    } catch (error) {
      console.error('Error loading proposal code:', error);
      setFormData((prev) => ({
        ...prev,
        proposalCode: 'CP-0001', // Default placeholder
      }));
      console.warn('Using placeholder proposal code due to auto-increment error');
    } finally {
      setIsLoadingProposalCode(false);
    }
  };

  const loadMasterData = async () => {
    try {
      const [leadsResponse, prospectsResponse] = await Promise.all([
        leadGenerationService.getAllLeads({ limit: 1000 }),
        prospectMasterService.getAllProspects({ limit: 1000 }),
      ]);

      if (leadsResponse.success) {
        setLeads(leadsResponse.data.leads || []);
      } else {
        console.warn('Failed to load leads:', leadsResponse.message);
      }

      if (prospectsResponse.success) {
        setProspects(prospectsResponse.data.prospects || []);
      } else {
        console.warn('Failed to load prospects:', prospectsResponse.message);
      }
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value,
      },
    }));
  };

  const handleArrayInputChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData((prev) => ({
      ...prev,
      [field]: newArray,
    }));
  };

  const handleAddArrayField = (field, defaultItem = '') => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], defaultItem],
    }));
  };

  const handleRemoveArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      [field]: newArray,
    }));
  };

  const handleAddService = () => {
    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, {
        serviceName: '',
        serviceDescription: '',
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0,
        serviceType: 'Software Development'
      }],
    }));
  };

  const handleServiceChange = (index, field, value) => {
    const newServices = [...formData.services];
    newServices[index] = { ...newServices[index], [field]: value };
    
    // Calculate total price
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? value : newServices[index].quantity;
      const unitPrice = field === 'unitPrice' ? value : newServices[index].unitPrice;
      newServices[index].totalPrice = quantity * unitPrice;
    }
    
    setFormData((prev) => ({
      ...prev,
      services: newServices,
    }));
  };

  const handleAddProduct = () => {
    setFormData((prev) => ({
      ...prev,
      products: [...prev.products, {
        productName: '',
        productDescription: '',
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0,
        productType: 'Software License'
      }],
    }));
  };

  const handleProductChange = (index, field, value) => {
    const newProducts = [...formData.products];
    newProducts[index] = { ...newProducts[index], [field]: value };
    
    // Calculate total price
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? value : newProducts[index].quantity;
      const unitPrice = field === 'unitPrice' ? value : newProducts[index].unitPrice;
      newProducts[index].totalPrice = quantity * unitPrice;
    }
    
    setFormData((prev) => ({
      ...prev,
      products: newProducts,
    }));
  };

  const calculateTotals = () => {
    const servicesTotal = formData.services.reduce((sum, service) => sum + (service.totalPrice || 0), 0);
    const productsTotal = formData.products.reduce((sum, product) => sum + (product.totalPrice || 0), 0);
    const totalValue = servicesTotal + productsTotal;
    const taxAmount = (totalValue * formData.taxRate) / 100;
    const discountAmount = (totalValue * formData.discountPercentage) / 100;
    const finalAmount = totalValue + taxAmount - discountAmount;

    setFormData((prev) => ({
      ...prev,
      totalValue,
      taxAmount,
      discountAmount,
      finalAmount,
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [formData.services, formData.products, formData.taxRate, formData.discountPercentage]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const proposalData = {
        ...formData,
        proposalDeliverables: formData.proposalDeliverables.filter(d => d.trim() !== ''),
        uniqueSellingPoints: formData.uniqueSellingPoints.filter(usp => usp.trim() !== ''),
        proposalTeam: formData.proposalTeam.filter(team => team.trim() !== ''),
        competitors: formData.competitors.filter(comp => comp.name.trim() !== ''),
        services: formData.services.filter(service => service.serviceName.trim() !== ''),
        products: formData.products.filter(product => product.productName.trim() !== ''),
      };

      const response = await commercialProposalService.createProposal(proposalData);
      if (response.success) {
        toast.success('Commercial proposal created successfully');
        navigate('/crm/commercial-proposal');
      } else {
        toast.error(response.message || 'Failed to create commercial proposal');
      }
    } catch (error) {
      console.error('Error creating commercial proposal:', error);
      toast.error('Failed to create commercial proposal');
    }
  };

  const handleReset = () => {
    setFormData({
      proposalCode: '',
      proposalDate: new Date().toISOString().split('T')[0],
      proposalTitle: '',
      proposalDescription: '',
      proposalType: 'Quote',
      proposalStatus: 'Draft',
      priority: 'Medium',
      lead: '',
      prospect: '',
      proposalScope: '',
      proposalObjectives: '',
      proposalDeliverables: [],
      proposalTimeline: '',
      proposalTerms: '',
      proposalConditions: '',
      totalValue: 0,
      currency: 'INR',
      taxRate: 18,
      discountPercentage: 0,
      paymentTerms: 'Advance',
      paymentSchedule: [],
      validFrom: new Date().toISOString().split('T')[0],
      validUntil: '',
      services: [],
      products: [],
      proposalOwner: '',
      proposalTeam: [],
      proposalManager: '',
      clientContact: {
        name: '',
        email: '',
        phone: '',
        designation: ''
      },
      clientDecisionMaker: {
        name: '',
        email: '',
        phone: '',
        designation: ''
      },
      clientRequirements: '',
      clientBudget: 0,
      clientTimeline: '',
      competitors: [],
      competitiveAdvantage: '',
      uniqueSellingPoints: [],
      followUpRequired: false,
      followUpDate: '',
      followUpNotes: '',
      nextSteps: '',
      expectedDecisionDate: '',
      probability: 0,
      attachments: [],
      proposalTemplate: '',
      approvalRequired: false
    });
    loadProposalCode();
  };

  const helpContent = (
    <>
      <h3>What is Commercial Proposal?</h3>
      <p>Commercial Proposal lets you <strong>create and manage new commercial proposals</strong>—quotes or proposals linked to leads and prospects, with scope, deliverables, financials, services/products, team, client contacts, and follow-up.</p>
      <h3>Tabs</h3>
      <p><strong>Proposal Info</strong> – Code, date, title, type, status, lead, prospect, scope, objectives, deliverables, timeline, terms.</p>
      <p><strong>Financial</strong> – Total value, currency, tax, discount, payment terms and schedule, validity.</p>
      <p><strong>Services &amp; Products</strong> – Line items for services and products.</p>
      <p><strong>Team &amp; Client</strong> – Proposal owner/team/manager and client contact/decision maker.</p>
      <p><strong>Competitive</strong> – Competitors, advantage, USPs.</p>
      <p><strong>Follow-up</strong> – Follow-up date/notes, next steps, expected decision date, probability.</p>
      <h3>How to use</h3>
      <p>Fill required fields and link to lead/prospect. Use <strong>Create Proposal</strong> to save. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  );

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/dashboard' },
          { label: 'CRM', path: '/crm' },
          { label: 'Commercial Proposal', path: '/crm/commercial-proposal' },
          { label: 'New', isCurrent: true },
        ]}
        rightSlot={
          <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
            <HelpCircle size={18} />
            <span className="d-none d-sm-inline">Help</span>
          </button>
        }
      />

      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title">
            <h1>Commercial Proposal</h1>
            <p>Create and manage new commercial proposal</p>
          </div>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Commercial Proposal ( Create )</h2>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'proposalInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('proposalInfo')}
          >
            <FileText size={18} />
            <span>Proposal Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'financial' ? 'active' : ''}`}
            onClick={() => setActiveTab('financial')}
          >
            <DollarSign size={18} />
            <span>Financial</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            <Briefcase size={18} />
            <span>Services & Products</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => setActiveTab('team')}
          >
            <Users size={18} />
            <span>Team & Client</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'competitive' ? 'active' : ''}`}
            onClick={() => setActiveTab('competitive')}
          >
            <Target size={18} />
            <span>Competitive</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'followUp' ? 'active' : ''}`}
            onClick={() => setActiveTab('followUp')}
          >
            <Calendar size={18} />
            <span>Follow-up</span>
          </button>
        </div>

        <form className="form-content" onSubmit={handleSubmit}>
          {/* Proposal Information Tab */}
          {activeTab === 'proposalInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Proposal Information</h3>
              </div>

              {/* Row 1: Proposal Code, Date, Type, Status */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="proposalCode" className="form-label required">
                    Proposal Code
                  </label>
                  <input
                    type="text"
                    id="proposalCode"
                    name="proposalCode"
                    value={isLoadingProposalCode ? 'Generating...' : formData.proposalCode}
                    readOnly
                    className="form-input"
                    placeholder="Enter proposal code"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="proposalDate" className="form-label required">
                    Proposal Date
                  </label>
                  <input
                    type="date"
                    id="proposalDate"
                    name="proposalDate"
                    value={formData.proposalDate}
                    onChange={(e) => handleInputChange('proposalDate', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="proposalType" className="form-label required">
                    Proposal Type
                  </label>
                  <select
                    id="proposalType"
                    name="proposalType"
                    value={formData.proposalType}
                    onChange={(e) => handleInputChange('proposalType', e.target.value)}
                    className="form-input"
                  >
                    <option value="Quote">Quote</option>
                    <option value="Proposal">Proposal</option>
                    <option value="RFP Response">RFP Response</option>
                    <option value="Tender">Tender</option>
                    <option value="Commercial Offer">Commercial Offer</option>
                    <option value="Price Estimate">Price Estimate</option>
                    <option value="Service Agreement">Service Agreement</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="proposalStatus" className="form-label required">
                    Status
                  </label>
                  <select
                    id="proposalStatus"
                    name="proposalStatus"
                    value={formData.proposalStatus}
                    onChange={(e) => handleInputChange('proposalStatus', e.target.value)}
                    className="form-input"
                  >
                    <option value="Draft">Draft</option>
                    <option value="Sent">Sent</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Negotiation">Negotiation</option>
                    <option value="Accepted">Accepted</option>
                    <option value="Rejected">Rejected</option>
                    <option value="Expired">Expired</option>
                    <option value="Withdrawn">Withdrawn</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Lead, Prospect, Priority */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="lead" className="form-label required">
                    Select Lead
                  </label>
                  <select
                    id="lead"
                    name="lead"
                    value={formData.lead}
                    onChange={(e) => handleInputChange('lead', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Lead</option>
                    {leads && leads.length > 0 ? (
                      leads.map((lead) => (
                        <option key={lead._id} value={lead._id}>
                          {lead.leadCode} - {lead.prospect?.prospectName || 'N/A'}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No leads available
                      </option>
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="prospect" className="form-label required">
                    Select Prospect
                  </label>
                  <select
                    id="prospect"
                    name="prospect"
                    value={formData.prospect}
                    onChange={(e) => handleInputChange('prospect', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Prospect</option>
                    {prospects && prospects.length > 0 ? (
                      prospects.map((prospect) => (
                        <option key={prospect._id} value={prospect._id}>
                          {prospect.prospectName} - {prospect.companyName}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No prospects available
                      </option>
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="priority" className="form-label required">
                    Priority
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="form-input"
                  >
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>

              {/* Row 3: Proposal Title */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="proposalTitle" className="form-label required">
                    Proposal Title
                  </label>
                  <input
                    type="text"
                    id="proposalTitle"
                    name="proposalTitle"
                    value={formData.proposalTitle}
                    onChange={(e) => handleInputChange('proposalTitle', e.target.value)}
                    className="form-input"
                    placeholder="Enter proposal title"
                  />
                </div>
              </div>

              {/* Row 4: Description, Scope */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="proposalDescription" className="form-label required">
                    Description
                  </label>
                  <textarea
                    id="proposalDescription"
                    name="proposalDescription"
                    value={formData.proposalDescription}
                    onChange={(e) => handleInputChange('proposalDescription', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Brief description of the proposal"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="proposalScope" className="form-label required">
                    Scope of Work
                  </label>
                  <textarea
                    id="proposalScope"
                    name="proposalScope"
                    value={formData.proposalScope}
                    onChange={(e) => handleInputChange('proposalScope', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Detailed scope of work"
                  />
                </div>
              </div>

              {/* Row 5: Objectives, Timeline */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="proposalObjectives" className="form-label">
                    Objectives
                  </label>
                  <textarea
                    id="proposalObjectives"
                    name="proposalObjectives"
                    value={formData.proposalObjectives}
                    onChange={(e) => handleInputChange('proposalObjectives', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Key objectives of the proposal"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="proposalTimeline" className="form-label">
                    Timeline
                  </label>
                  <input
                    type="text"
                    id="proposalTimeline"
                    name="proposalTimeline"
                    value={formData.proposalTimeline}
                    onChange={(e) => handleInputChange('proposalTimeline', e.target.value)}
                    className="form-input"
                    placeholder="e.g., 3 months, 6 weeks"
                  />
                </div>
              </div>

              {/* Row 6: Terms, Conditions */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="proposalTerms" className="form-label">
                    Terms & Conditions
                  </label>
                  <textarea
                    id="proposalTerms"
                    name="proposalTerms"
                    value={formData.proposalTerms}
                    onChange={(e) => handleInputChange('proposalTerms', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Terms and conditions"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="proposalConditions" className="form-label">
                    Special Conditions
                  </label>
                  <textarea
                    id="proposalConditions"
                    name="proposalConditions"
                    value={formData.proposalConditions}
                    onChange={(e) => handleInputChange('proposalConditions', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Any special conditions"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Financial Tab */}
          {activeTab === 'financial' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Financial Information</h3>
              </div>

              {/* Row 1: Currency, Tax Rate, Discount */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="currency" className="form-label required">
                    Currency
                  </label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                    className="form-input"
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="taxRate" className="form-label">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    id="taxRate"
                    name="taxRate"
                    value={formData.taxRate}
                    onChange={(e) => handleInputChange('taxRate', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="discountPercentage" className="form-label">
                    Discount (%)
                  </label>
                  <input
                    type="number"
                    id="discountPercentage"
                    name="discountPercentage"
                    value={formData.discountPercentage}
                    onChange={(e) => handleInputChange('discountPercentage', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Row 2: Payment Terms, Validity */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="paymentTerms" className="form-label required">
                    Payment Terms
                  </label>
                  <select
                    id="paymentTerms"
                    name="paymentTerms"
                    value={formData.paymentTerms}
                    onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                    className="form-input"
                  >
                    <option value="Advance">Advance</option>
                    <option value="Milestone">Milestone</option>
                    <option value="On Delivery">On Delivery</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="validFrom" className="form-label required">
                    Valid From
                  </label>
                  <input
                    type="date"
                    id="validFrom"
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={(e) => handleInputChange('validFrom', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="validUntil" className="form-label required">
                    Valid Until
                  </label>
                  <input
                    type="date"
                    id="validUntil"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={(e) => handleInputChange('validUntil', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Financial Summary */}
              <div className="section-header mt-6">
                <h3>Financial Summary</h3>
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label className="form-label">Services Total</label>
                  <input
                    type="text"
                    value={formatRupeeAmount(formData.services.reduce((sum, service) => sum + (service.totalPrice || 0), 0))}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Products Total</label>
                  <input
                    type="text"
                    value={formatRupeeAmount(formData.products.reduce((sum, product) => sum + (product.totalPrice || 0), 0))}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Subtotal</label>
                  <input
                    type="text"
                    value={formatRupeeAmount(formData.totalValue)}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tax Amount</label>
                  <input
                    type="text"
                    value={formatRupeeAmount(formData.taxAmount)}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                  />
                </div>
              </div>

              <div className="grid-row-2">
                <div className="form-group">
                  <label className="form-label">Discount Amount</label>
                  <input
                    type="text"
                    value={formatRupeeAmount(formData.discountAmount)}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label font-bold text-lg">Final Amount</label>
                  <input
                    type="text"
                    value={formatRupeeAmount(formData.finalAmount)}
                    readOnly
                    className="form-input font-bold text-lg"
                    style={{ backgroundColor: '#F0EBF7', color: '#1e40af', borderColor: '#3b82f6' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Services & Products Tab */}
          {activeTab === 'services' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Services & Products</h3>
                <p className="section-description">Define the services and products included in this proposal with detailed pricing</p>
              </div>

              {/* Services Section */}
              <div className="services-products-section">
                <div className="section-card">
                  <div className="section-card-header">
                    <div className="section-card-title">
                      <Briefcase size={20} className="section-icon" />
                      <div>
                        <h4>Services</h4>
                        <p>Add professional services to your proposal</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-add-service"
                      onClick={handleAddService}
                    >
                      <Plus size={16} />
                      Add Service
                    </button>
                  </div>

                  <div className="services-list">
                    {formData.services.length === 0 ? (
                      <div className="empty-state">
                        <Briefcase size={48} className="empty-icon" />
                        <h4>No Services Added</h4>
                        <p>Start by adding your first service to this proposal</p>
                        <button
                          type="button"
                          className="btn-add-first"
                          onClick={handleAddService}
                        >
                          <Plus size={16} />
                          Add Your First Service
                        </button>
                      </div>
                    ) : (
                      formData.services.map((service, index) => (
                        <div key={index} className="service-item">
                          <div className="service-item-header">
                            <div className="service-item-title">
                              <span className="service-number">#{index + 1}</span>
                              <h5>{service.serviceName || 'Untitled Service'}</h5>
                            </div>
                            <button
                              type="button"
                              className="btn-remove-service"
                              onClick={() => handleRemoveArrayField('services', index)}
                            >
                              <X size={16} />
                            </button>
                          </div>

                          <div className="service-item-content">
                            <div className="service-grid">
                              <div className="service-field">
                                <label className="service-label">Service Name</label>
                                <input
                                  type="text"
                                  value={service.serviceName}
                                  onChange={(e) => handleServiceChange(index, 'serviceName', e.target.value)}
                                  className="service-input"
                                  placeholder="Enter service name"
                                />
                              </div>

                              <div className="service-field">
                                <label className="service-label">Service Type</label>
                                <select
                                  value={service.serviceType}
                                  onChange={(e) => handleServiceChange(index, 'serviceType', e.target.value)}
                                  className="service-input"
                                >
                                  <option value="Software Development">Software Development</option>
                                  <option value="Consulting">Consulting</option>
                                  <option value="Support">Support</option>
                                  <option value="Training">Training</option>
                                  <option value="Implementation">Implementation</option>
                                  <option value="Custom">Custom</option>
                                </select>
                              </div>

                              <div className="service-field">
                                <label className="service-label">Quantity</label>
                                <input
                                  type="number"
                                  value={service.quantity}
                                  onChange={(e) => handleServiceChange(index, 'quantity', parseInt(e.target.value) || 0)}
                                  className="service-input"
                                  min="1"
                                />
                              </div>

                              <div className="service-field">
                                <label className="service-label">Unit Price (₹)</label>
                                <input
                                  type="number"
                                  value={service.unitPrice}
                                  onChange={(e) => handleServiceChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                  className="service-input"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>

                            <div className="service-description">
                              <label className="service-label">Description</label>
                              <textarea
                                value={service.serviceDescription}
                                onChange={(e) => handleServiceChange(index, 'serviceDescription', e.target.value)}
                                className="service-textarea"
                                rows="2"
                                placeholder="Describe the service in detail..."
                              />
                            </div>

                            <div className="service-total">
                              <div className="service-total-label">Total Price</div>
                              <div className="service-total-amount">{formatRupeeAmount(service.totalPrice)}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Products Section */}
                <div className="section-card">
                  <div className="section-card-header">
                    <div className="section-card-title">
                      <Cpu size={20} className="section-icon" />
                      <div>
                        <h4>Products</h4>
                        <p>Add products and licenses to your proposal</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-add-product"
                      onClick={handleAddProduct}
                    >
                      <Plus size={16} />
                      Add Product
                    </button>
                  </div>

                  <div className="products-list">
                    {formData.products.length === 0 ? (
                      <div className="empty-state">
                        <Cpu size={48} className="empty-icon" />
                        <h4>No Products Added</h4>
                        <p>Add products, licenses, or hardware to this proposal</p>
                        <button
                          type="button"
                          className="btn-add-first"
                          onClick={handleAddProduct}
                        >
                          <Plus size={16} />
                          Add Your First Product
                        </button>
                      </div>
                    ) : (
                      formData.products.map((product, index) => (
                        <div key={index} className="product-item">
                          <div className="product-item-header">
                            <div className="product-item-title">
                              <span className="product-number">#{index + 1}</span>
                              <h5>{product.productName || 'Untitled Product'}</h5>
                            </div>
                            <button
                              type="button"
                              className="btn-remove-product"
                              onClick={() => handleRemoveArrayField('products', index)}
                            >
                              <X size={16} />
                            </button>
                          </div>

                          <div className="product-item-content">
                            <div className="product-grid">
                              <div className="product-field">
                                <label className="product-label">Product Name</label>
                                <input
                                  type="text"
                                  value={product.productName}
                                  onChange={(e) => handleProductChange(index, 'productName', e.target.value)}
                                  className="product-input"
                                  placeholder="Enter product name"
                                />
                              </div>

                              <div className="product-field">
                                <label className="product-label">Product Type</label>
                                <select
                                  value={product.productType}
                                  onChange={(e) => handleProductChange(index, 'productType', e.target.value)}
                                  className="product-input"
                                >
                                  <option value="Software License">Software License</option>
                                  <option value="Hardware">Hardware</option>
                                  <option value="Subscription">Subscription</option>
                                  <option value="Custom">Custom</option>
                                </select>
                              </div>

                              <div className="product-field">
                                <label className="product-label">Quantity</label>
                                <input
                                  type="number"
                                  value={product.quantity}
                                  onChange={(e) => handleProductChange(index, 'quantity', parseInt(e.target.value) || 0)}
                                  className="product-input"
                                  min="1"
                                />
                              </div>

                              <div className="product-field">
                                <label className="product-label">Unit Price (₹)</label>
                                <input
                                  type="number"
                                  value={product.unitPrice}
                                  onChange={(e) => handleProductChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                  className="product-input"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>

                            <div className="product-description">
                              <label className="product-label">Description</label>
                              <textarea
                                value={product.productDescription}
                                onChange={(e) => handleProductChange(index, 'productDescription', e.target.value)}
                                className="product-textarea"
                                rows="2"
                                placeholder="Describe the product in detail..."
                              />
                            </div>

                            <div className="product-total">
                              <div className="product-total-label">Total Price</div>
                              <div className="product-total-amount">{formatRupeeAmount(product.totalPrice)}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Summary Section */}
                <div className="summary-card">
                  <div className="summary-header">
                    <h4>Proposal Summary</h4>
                    <p>Financial overview of your proposal</p>
                  </div>
                  <div className="summary-content">
                    <div className="summary-row">
                      <span className="summary-label">Services Total</span>
                      <span className="summary-value">{formatRupeeAmount(formData.services.reduce((sum, service) => sum + (service.totalPrice || 0), 0))}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Products Total</span>
                      <span className="summary-value">{formatRupeeAmount(formData.products.reduce((sum, product) => sum + (product.totalPrice || 0), 0))}</span>
                    </div>
                    <div className="summary-row total">
                      <span className="summary-label">Subtotal</span>
                      <span className="summary-value">{formatRupeeAmount(formData.totalValue)}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Tax ({formData.taxRate}%)</span>
                      <span className="summary-value">{formatRupeeAmount(formData.taxAmount)}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Discount ({formData.discountPercentage}%)</span>
                      <span className="summary-value">{formatINR(-Math.abs(formData.discountAmount || 0))}</span>
                    </div>
                    <div className="summary-row final">
                      <span className="summary-label">Final Amount</span>
                      <span className="summary-value">{formatRupeeAmount(formData.finalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Team & Client Tab */}
          {activeTab === 'team' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Team & Client Information</h3>
              </div>

              {/* Team Section */}
              <div className="mb-8">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Team Assignment</h4>
                
                <div className="grid-row-2">
                  <div className="form-group">
                    <label htmlFor="proposalOwner" className="form-label required">
                      Proposal Owner
                    </label>
                    <select
                      id="proposalOwner"
                      name="proposalOwner"
                      value={formData.proposalOwner}
                      onChange={(e) => handleInputChange('proposalOwner', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select Owner</option>
                      {users && users.length > 0 ? (
                        users.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.firstName} {user.lastName}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No users available
                        </option>
                      )}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="proposalManager" className="form-label">
                      Proposal Manager
                    </label>
                    <select
                      id="proposalManager"
                      name="proposalManager"
                      value={formData.proposalManager}
                      onChange={(e) => handleInputChange('proposalManager', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select Manager</option>
                      {users && users.length > 0 ? (
                        users.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.firstName} {user.lastName}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No users available
                        </option>
                      )}
                    </select>
                  </div>
                </div>
              </div>

              {/* Client Contact Section */}
              <div className="mb-8">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Client Contact</h4>
                
                <div className="grid-row-2">
                  <div className="form-group">
                    <label htmlFor="clientContactName" className="form-label">
                      Contact Name
                    </label>
                    <input
                      type="text"
                      id="clientContactName"
                      value={formData.clientContact.name}
                      onChange={(e) => handleNestedInputChange('clientContact', 'name', e.target.value)}
                      className="form-input"
                      placeholder="Client contact name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="clientContactEmail" className="form-label">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      id="clientContactEmail"
                      value={formData.clientContact.email}
                      onChange={(e) => handleNestedInputChange('clientContact', 'email', e.target.value)}
                      className="form-input"
                      placeholder="Client contact email"
                    />
                  </div>
                </div>

                <div className="grid-row-2">
                  <div className="form-group">
                    <label htmlFor="clientContactPhone" className="form-label">
                      Contact Phone
                    </label>
                    <input
                      type="text"
                      id="clientContactPhone"
                      value={formData.clientContact.phone}
                      onChange={(e) => handleNestedInputChange('clientContact', 'phone', e.target.value)}
                      className="form-input"
                      placeholder="Client contact phone"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="clientContactDesignation" className="form-label">
                      Designation
                    </label>
                    <input
                      type="text"
                      id="clientContactDesignation"
                      value={formData.clientContact.designation}
                      onChange={(e) => handleNestedInputChange('clientContact', 'designation', e.target.value)}
                      className="form-input"
                      placeholder="Client contact designation"
                    />
                  </div>
                </div>
              </div>

              {/* Decision Maker Section */}
              <div className="mb-8">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Decision Maker</h4>
                
                <div className="grid-row-2">
                  <div className="form-group">
                    <label htmlFor="decisionMakerName" className="form-label">
                      Decision Maker Name
                    </label>
                    <input
                      type="text"
                      id="decisionMakerName"
                      value={formData.clientDecisionMaker.name}
                      onChange={(e) => handleNestedInputChange('clientDecisionMaker', 'name', e.target.value)}
                      className="form-input"
                      placeholder="Decision maker name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="decisionMakerEmail" className="form-label">
                      Decision Maker Email
                    </label>
                    <input
                      type="email"
                      id="decisionMakerEmail"
                      value={formData.clientDecisionMaker.email}
                      onChange={(e) => handleNestedInputChange('clientDecisionMaker', 'email', e.target.value)}
                      className="form-input"
                      placeholder="Decision maker email"
                    />
                  </div>
                </div>

                <div className="grid-row-2">
                  <div className="form-group">
                    <label htmlFor="decisionMakerPhone" className="form-label">
                      Decision Maker Phone
                    </label>
                    <input
                      type="text"
                      id="decisionMakerPhone"
                      value={formData.clientDecisionMaker.phone}
                      onChange={(e) => handleNestedInputChange('clientDecisionMaker', 'phone', e.target.value)}
                      className="form-input"
                      placeholder="Decision maker phone"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="decisionMakerDesignation" className="form-label">
                      Designation
                    </label>
                    <input
                      type="text"
                      id="decisionMakerDesignation"
                      value={formData.clientDecisionMaker.designation}
                      onChange={(e) => handleNestedInputChange('clientDecisionMaker', 'designation', e.target.value)}
                      className="form-input"
                      placeholder="Decision maker designation"
                    />
                  </div>
                </div>
              </div>

              {/* Client Requirements */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="clientRequirements" className="form-label">
                    Client Requirements
                  </label>
                  <textarea
                    id="clientRequirements"
                    value={formData.clientRequirements}
                    onChange={(e) => handleInputChange('clientRequirements', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Detailed client requirements"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="clientTimeline" className="form-label">
                    Client Timeline
                  </label>
                  <input
                    type="text"
                    id="clientTimeline"
                    value={formData.clientTimeline}
                    onChange={(e) => handleInputChange('clientTimeline', e.target.value)}
                    className="form-input"
                    placeholder="Client's expected timeline"
                  />
                </div>
              </div>

              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="clientBudget" className="form-label">
                    Client Budget
                  </label>
                  <input
                    type="number"
                    id="clientBudget"
                    value={formData.clientBudget}
                    onChange={(e) => handleInputChange('clientBudget', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    placeholder="Client's budget range"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Competitive Tab */}
          {activeTab === 'competitive' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Competitive Analysis</h3>
                <p className="section-description">Analyze your competitive position and unique value propositions</p>
              </div>

              <div className="competitive-analysis-section">
                {/* Competitive Advantage Card */}
                <div className="section-card">
                  <div className="section-card-header">
                    <div className="section-card-title">
                      <TrendingUp size={20} className="section-icon" />
                      <div>
                        <h4>Competitive Advantage</h4>
                        <p>Describe what sets you apart from competitors</p>
                      </div>
                    </div>
                  </div>
                  <div className="advantage-content">
                    <textarea
                      id="competitiveAdvantage"
                      value={formData.competitiveAdvantage}
                      onChange={(e) => handleInputChange('competitiveAdvantage', e.target.value)}
                      className="advantage-textarea"
                      rows="4"
                      placeholder="Describe your key competitive advantages and differentiators..."
                    />
                  </div>
                </div>

                {/* Unique Selling Points Card */}
                <div className="section-card">
                  <div className="section-card-header">
                    <div className="section-card-title">
                      <Target size={20} className="section-icon" />
                      <div>
                        <h4>Unique Selling Points</h4>
                        <p>Highlight your key differentiators and value propositions</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-add-usp"
                      onClick={() => handleAddArrayField('uniqueSellingPoints')}
                    >
                      <Plus size={16} />
                      Add USP
                    </button>
                  </div>

                  <div className="usp-list">
                    {formData.uniqueSellingPoints.length === 0 ? (
                      <div className="empty-state">
                        <Target size={48} className="empty-icon" />
                        <h4>No USPs Added</h4>
                        <p>Start by adding your unique selling points</p>
                        <button
                          type="button"
                          className="btn-add-first"
                          onClick={() => handleAddArrayField('uniqueSellingPoints')}
                        >
                          <Plus size={16} />
                          Add Your First USP
                        </button>
                      </div>
                    ) : (
                      formData.uniqueSellingPoints.map((usp, index) => (
                        <div key={index} className="usp-item">
                          <div className="usp-item-header">
                            <div className="usp-item-title">
                              <span className="usp-number">#{index + 1}</span>
                              <h5>{usp || 'Untitled USP'}</h5>
                            </div>
                            <button
                              type="button"
                              className="btn-remove-usp"
                              onClick={() => handleRemoveArrayField('uniqueSellingPoints', index)}
                            >
                              <X size={16} />
                            </button>
                          </div>
                          <div className="usp-item-content">
                            <input
                              type="text"
                              value={usp}
                              onChange={(e) => handleArrayInputChange('uniqueSellingPoints', index, e.target.value)}
                              className="usp-input"
                              placeholder="Enter your unique selling point..."
                            />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Competitors Card */}
                <div className="section-card">
                  <div className="section-card-header">
                    <div className="section-card-title">
                      <AlertCircle size={20} className="section-icon" />
                      <div>
                        <h4>Competitors</h4>
                        <p>Analyze your competition and their positioning</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-add-competitor"
                      onClick={() => handleAddArrayField('competitors', { name: '', proposal: '', advantages: '', disadvantages: '' })}
                    >
                      <Plus size={16} />
                      Add Competitor
                    </button>
                  </div>

                  <div className="competitors-list">
                    {formData.competitors.length === 0 ? (
                      <div className="empty-state">
                        <AlertCircle size={48} className="empty-icon" />
                        <h4>No Competitors Added</h4>
                        <p>Add your main competitors for analysis</p>
                        <button
                          type="button"
                          className="btn-add-first"
                          onClick={() => handleAddArrayField('competitors', { name: '', proposal: '', advantages: '', disadvantages: '' })}
                        >
                          <Plus size={16} />
                          Add Your First Competitor
                        </button>
                      </div>
                    ) : (
                      formData.competitors.map((competitor, index) => (
                        <div key={index} className="competitor-item">
                          <div className="competitor-item-header">
                            <div className="competitor-item-title">
                              <span className="competitor-number">#{index + 1}</span>
                              <h5>{competitor.name || 'Untitled Competitor'}</h5>
                            </div>
                            <button
                              type="button"
                              className="btn-remove-competitor"
                              onClick={() => handleRemoveArrayField('competitors', index)}
                            >
                              <X size={16} />
                            </button>
                          </div>

                          <div className="competitor-item-content">
                            <div className="competitor-grid">
                              <div className="competitor-field">
                                <label className="competitor-label">Competitor Name</label>
                                <input
                                  type="text"
                                  value={competitor.name}
                                  onChange={(e) => {
                                    const newCompetitors = [...formData.competitors];
                                    newCompetitors[index] = { ...newCompetitors[index], name: e.target.value };
                                    setFormData(prev => ({ ...prev, competitors: newCompetitors }));
                                  }}
                                  className="competitor-input"
                                  placeholder="Enter competitor name"
                                />
                              </div>

                              <div className="competitor-field">
                                <label className="competitor-label">Their Proposal</label>
                                <input
                                  type="text"
                                  value={competitor.proposal}
                                  onChange={(e) => {
                                    const newCompetitors = [...formData.competitors];
                                    newCompetitors[index] = { ...newCompetitors[index], proposal: e.target.value };
                                    setFormData(prev => ({ ...prev, competitors: newCompetitors }));
                                  }}
                                  className="competitor-input"
                                  placeholder="Their proposal details"
                                />
                              </div>
                            </div>

                            <div className="competitor-analysis">
                              <div className="competitor-field">
                                <label className="competitor-label">Their Advantages</label>
                                <textarea
                                  value={competitor.advantages}
                                  onChange={(e) => {
                                    const newCompetitors = [...formData.competitors];
                                    newCompetitors[index] = { ...newCompetitors[index], advantages: e.target.value };
                                    setFormData(prev => ({ ...prev, competitors: newCompetitors }));
                                  }}
                                  className="competitor-textarea"
                                  rows="2"
                                  placeholder="What advantages do they have?"
                                />
                              </div>

                              <div className="competitor-field">
                                <label className="competitor-label">Their Disadvantages</label>
                                <textarea
                                  value={competitor.disadvantages}
                                  onChange={(e) => {
                                    const newCompetitors = [...formData.competitors];
                                    newCompetitors[index] = { ...newCompetitors[index], disadvantages: e.target.value };
                                    setFormData(prev => ({ ...prev, competitors: newCompetitors }));
                                  }}
                                  className="competitor-textarea"
                                  rows="2"
                                  placeholder="What are their weaknesses?"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Follow-up Tab */}
          {activeTab === 'followUp' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Follow-up & Tracking</h3>
              </div>

              {/* Follow-up Information */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="followUpRequired" className="form-label">
                    Follow-up Required
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="followUpRequired"
                      checked={formData.followUpRequired}
                      onChange={(e) => handleInputChange('followUpRequired', e.target.checked)}
                      className="form-checkbox"
                    />
                    <label htmlFor="followUpRequired" className="text-sm text-gray-700">
                      Mark if follow-up is required
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="followUpDate" className="form-label">
                    Follow-up Date
                  </label>
                  <input
                    type="date"
                    id="followUpDate"
                    value={formData.followUpDate}
                    onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                    className="form-input"
                    disabled={!formData.followUpRequired}
                  />
                </div>
              </div>

              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="expectedDecisionDate" className="form-label">
                    Expected Decision Date
                  </label>
                  <input
                    type="date"
                    id="expectedDecisionDate"
                    value={formData.expectedDecisionDate}
                    onChange={(e) => handleInputChange('expectedDecisionDate', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="probability" className="form-label">
                    Probability of Success (%)
                  </label>
                  <input
                    type="number"
                    id="probability"
                    value={formData.probability}
                    onChange={(e) => handleInputChange('probability', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="followUpNotes" className="form-label">
                    Follow-up Notes
                  </label>
                  <textarea
                    id="followUpNotes"
                    value={formData.followUpNotes}
                    onChange={(e) => handleInputChange('followUpNotes', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Follow-up notes and observations"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="nextSteps" className="form-label">
                    Next Steps
                  </label>
                  <textarea
                    id="nextSteps"
                    value={formData.nextSteps}
                    onChange={(e) => handleInputChange('nextSteps', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Next steps and actions"
                  />
                </div>
              </div>

              {/* Approval Section */}
              <div className="section-header mt-6">
                <h3>Approval & Workflow</h3>
              </div>

              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="approvalRequired" className="form-label">
                    Approval Required
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="approvalRequired"
                      checked={formData.approvalRequired}
                      onChange={(e) => handleInputChange('approvalRequired', e.target.checked)}
                      className="form-checkbox"
                    />
                    <label htmlFor="approvalRequired" className="text-sm text-gray-700">
                      This proposal requires approval before sending
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleReset}>
              <RotateCcw size={16} />
              Reset
            </button>
            <button type="submit" className="btn-primary">
              <Save size={16} />
              Create Proposal
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Commercial Proposal – Help">
        {helpContent}
      </HelpModal>
    </div>
  );
};

export default CommercialProposalForm;
